import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, MinusCircle, Trash2, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DeleteSolutionsDialog } from "@/components/dashboard/DeleteSolutionsDialog";
import { SolutionDetailDrawer } from "@/components/dashboard/SolutionDetailDrawer";
import {
  type FilterOptions,
  SolutionsFilterBar,
} from "@/components/dashboard/solutions-filters/SolutionsFilterBar";
import { ConcernCountBadge } from "@/components/plans/ConcernCountBadge";
import { FitBadge } from "@/components/shared/FitBadge";
import { IntensityHoursHint } from "@/components/shared/IntensityHoursHint";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { SearchInput } from "@/components/shared/search-input";
import { useSolutionFilters } from "@/hooks/use-solution-filters";
import { useSolutionDeletionAllowed } from "@/hooks/use-system-config";
import { useSession } from "@/lib/auth-client";
import { jsonFetch } from "@/lib/json-fetch";
import { type PositionDisciplines, positionShortLabel, positionTitle } from "@/lib/position-label";
import { matchesFilters } from "@/lib/solution-filters";

type FitTier = "Strong" | "Moderate" | "At Risk";

interface SolutionRow {
  id: string;
  name: string;
  status: string;
  intensity: number;
  podSize: "Solo" | "Pair" | "Trio";
  account: { id: string; name: string; tier: string; expectationLevel: number };
  assignments: Array<{
    id: string;
    role: string;
    resource: { id: string; name: string; initials: string };
    points: number;
  }>;
  constraints: Array<{ id: string; name: string }>;
  positionDisciplines: PositionDisciplines;
  fit: { tier: FitTier; score: number } | null;
  /** RP-34 — unresolved Staffing Concerns on the Solution's live plans. */
  concerns?: { unresolved: number; blockingOpen: number };
}

interface DashboardData {
  alerts: { atRisk: number; unassigned: number; partial: number; strong: number };
  solutions: SolutionRow[];
}

interface AlertCardProps {
  icon: typeof AlertTriangle;
  label: string;
  count: number | string;
  tone: "danger" | "warn" | "info" | "good";
  active?: boolean;
  onClick?: () => void;
}

function AlertCard({ icon: Icon, label, count, tone, active, onClick }: AlertCardProps) {
  const toneClasses = {
    danger: "border-[var(--fit-at-risk)]/30 bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]",
    warn: "border-[var(--fit-moderate)]/30 bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]",
    info: "border-border bg-card text-foreground",
    good: "border-[var(--fit-strong)]/30 bg-[var(--fit-strong-bg)] text-[var(--fit-strong)]",
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-w-0 items-center gap-3 rounded-md border p-3 text-left transition ${toneClasses} ${active ? "ring-2 ring-foreground/40" : "hover:brightness-95"}`}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
        <span className="min-w-0 truncate font-mono text-xs font-medium tracking-[0.08em] uppercase opacity-80">
          {label}
        </span>
        <span className="font-display text-2xl font-bold leading-none">{count}</span>
      </div>
    </button>
  );
}

function PodChips({
  podSize,
  assignments,
  positionDisciplines,
}: {
  podSize: "Solo" | "Pair" | "Trio";
  assignments: SolutionRow["assignments"];
  positionDisciplines: PositionDisciplines;
}) {
  const roles: ("R1" | "R2" | "R3")[] =
    podSize === "Solo" ? ["R1"] : podSize === "Pair" ? ["R1", "R2"] : ["R1", "R2", "R3"];
  const byRole = new Map(assignments.map((a) => [a.role, a]));
  return (
    <div className="flex items-center gap-1">
      {roles.map((role) => {
        const a = byRole.get(role);
        return a ? (
          <span
            key={role}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold"
            title={`${positionTitle(role, positionDisciplines)} — ${a.resource.name}`}
          >
            {/* Tight: the seat's Discipline replaces "R1"; the full seat is in the tooltip. */}
            <span className="rounded-full bg-foreground/85 px-1 text-xs text-background">
              {positionShortLabel(role, positionDisciplines)}
            </span>
            <span className="text-foreground/80">{a.resource.initials}</span>
          </span>
        ) : (
          <span
            key={role}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-1.5 py-0.5 text-xs font-semibold text-muted-foreground"
            title={`${positionTitle(role, positionDisciplines)} — open`}
          >
            <span className="rounded-full bg-muted px-1 text-xs">
              {positionShortLabel(role, positionDisciplines)}
            </span>
            <span>—</span>
          </span>
        );
      })}
    </div>
  );
}

function TierTag({ tier }: { tier: string }) {
  const map: Record<string, string> = {
    Support: "bg-info-100 text-info-700",
    Growth: "bg-brand-50 text-brand-900",
    Standard: "bg-brand-100 text-brand-700",
    Strategic: "bg-brand-200 text-brand-900",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs font-medium ${map[tier] ?? "bg-muted text-foreground"}`}
    >
      {tier}
    </span>
  );
}

type SortKey = "name" | "account" | "tier" | "intensity" | "fit" | "status";
type SortDir = "asc" | "desc";

const TIER_ORDER: Record<string, number> = {
  Emerging: 1,
  Established: 2,
  Experienced: 3,
  Transformational: 4,
};

const FIT_TIER_ORDER: Record<FitTier, number> = {
  "At Risk": 1,
  Moderate: 2,
  Strong: 3,
};

function compareRows(a: SolutionRow, b: SolutionRow, key: SortKey): number {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name);
    case "account":
      return a.account.name.localeCompare(b.account.name);
    case "tier":
      return (TIER_ORDER[a.account.tier] ?? 0) - (TIER_ORDER[b.account.tier] ?? 0);
    case "intensity":
      return a.intensity - b.intensity;
    case "fit": {
      const av = a.fit ? a.fit.score : -1;
      const bv = b.fit ? b.fit.score : -1;
      if (av !== bv) return av - bv;
      const at = a.fit ? FIT_TIER_ORDER[a.fit.tier] : 0;
      const bt = b.fit ? FIT_TIER_ORDER[b.fit.tier] : 0;
      return at - bt;
    }
    case "status":
      return a.status.localeCompare(b.status);
  }
}

function SortHeader({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: boolean;
  dir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  return (
    <th className="px-3 py-2 font-semibold">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 transition ${active ? "text-foreground" : "hover:text-foreground"}`}
      >
        <span>{label}</span>
        <span className="text-xs leading-none opacity-40" aria-hidden="true">
          {active ? (dir === "asc" ? "▲" : "▼") : "▾"}
        </span>
      </button>
    </th>
  );
}

export function DashboardPage({ initialSolutionId }: { initialSolutionId?: string } = {}) {
  const { state: filters, setState: setFilters, reset: resetFilters } = useSolutionFilters();
  const [openId, setOpenId] = useState<string | null>(initialSolutionId ?? null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const { data: session } = useSession();
  const deletionAllowed = useSolutionDeletionAllowed();
  const canDelete = session?.user?.role === "RM" && deletionAllowed;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ["dashboard-solutions"],
    queryFn: () => jsonFetch<DashboardData>("/api/dashboard/solutions"),
  });

  const options = useMemo<FilterOptions>(() => {
    const tiers = new Set<string>();
    const constraints = new Map<string, string>();
    const resources = new Map<string, string>();
    for (const s of data?.solutions ?? []) {
      tiers.add(s.account.tier);
      for (const c of s.constraints) constraints.set(c.id, c.name);
      for (const a of s.assignments) resources.set(a.resource.id, a.resource.name);
    }
    const sortByName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    return {
      tiers: [...tiers].sort((a, b) => a.localeCompare(b)),
      constraints: [...constraints].map(([id, name]) => ({ id, name })).sort(sortByName),
      resources: [...resources].map(([id, name]) => ({ id, name })).sort(sortByName),
    };
  }, [data]);

  const handleSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const filtered = useMemo<SolutionRow[]>(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const rows = (data.solutions ?? []).filter((s) => {
      if (q) {
        const haystack = `${s.name} ${s.account.name} ${s.status}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // SolutionRow structurally satisfies FilterableSolution (superset) — no cast needed.
      return matchesFilters(s, filters);
    });
    const sorted = [...rows].sort((a, b) => compareRows(a, b, sortKey));
    if (sortDir === "desc") sorted.reverse();
    return sorted;
  }, [data, filters, search, sortKey, sortDir]);

  // Drop selections no longer visible under the current filters/search.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(filtered.map((s) => s.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Manage"
        title="Solutions"
        description="Triage staffing risk, filter the portfolio, and assign Pods inline."
      />

      <div className="flex flex-wrap items-start gap-3">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search solutions, accounts…"
          className="w-64"
        />
        <div className="min-w-0 flex-1">
          <SolutionsFilterBar
            state={filters}
            setState={setFilters}
            reset={resetFilters}
            options={options}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <AlertCard
          icon={XCircle}
          label="At Risk"
          count={data?.alerts.atRisk ?? "—"}
          tone="danger"
          active={filters.preset === "atRisk"}
          onClick={() =>
            setFilters({ ...filters, preset: filters.preset === "atRisk" ? "all" : "atRisk" })
          }
        />
        <AlertCard
          icon={AlertTriangle}
          label="Unassigned"
          count={data?.alerts.unassigned ?? "—"}
          tone="warn"
          active={filters.preset === "unassigned"}
          onClick={() =>
            setFilters({
              ...filters,
              preset: filters.preset === "unassigned" ? "all" : "unassigned",
            })
          }
        />
        <AlertCard
          icon={MinusCircle}
          label="Partial"
          count={data?.alerts.partial ?? "—"}
          tone="info"
          active={filters.preset === "partial"}
          onClick={() =>
            setFilters({ ...filters, preset: filters.preset === "partial" ? "all" : "partial" })
          }
        />
        <AlertCard
          icon={CheckCircle2}
          label="Strong"
          count={data?.alerts.strong ?? "—"}
          tone="good"
          active={filters.preset === "strong"}
          onClick={() =>
            setFilters({ ...filters, preset: filters.preset === "strong" ? "all" : "strong" })
          }
        />
      </div>

      {isLoading && (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      )}
      {error && (
        <div className="rounded-md border border-[var(--fit-at-risk)]/30 bg-[var(--fit-at-risk-bg)] px-6 py-4 text-sm text-[var(--fit-at-risk)]">
          Failed to load solutions.
        </div>
      )}

      {data && (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {canDelete && (
                  <th className="w-8 px-2">
                    <input
                      type="checkbox"
                      aria-label="Select all filtered Solutions"
                      className="h-4 w-4 accent-[var(--fit-at-risk)]"
                      checked={filtered.length > 0 && filtered.every((s) => selected.has(s.id))}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? new Set(filtered.map((s) => s.id)) : new Set(),
                        )
                      }
                    />
                  </th>
                )}
                <SortHeader
                  label="Solution"
                  sortKey="name"
                  active={sortKey === "name"}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Account"
                  sortKey="account"
                  active={sortKey === "account"}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Tier"
                  sortKey="tier"
                  active={sortKey === "tier"}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Intensity"
                  sortKey="intensity"
                  active={sortKey === "intensity"}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <th className="px-3 py-2 font-semibold">Pod</th>
                <SortHeader
                  label="Fit"
                  sortKey="fit"
                  active={sortKey === "fit"}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Status"
                  sortKey="status"
                  active={sortKey === "status"}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => setOpenId(s.id)}
                  className="cursor-pointer border-t border-border hover:bg-muted/40"
                >
                  {canDelete && (
                    <td className="px-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${s.name}`}
                        className="h-4 w-4 accent-[var(--fit-at-risk)]"
                        checked={selected.has(s.id)}
                        onChange={() => toggleOne(s.id)}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium text-foreground">
                    {s.name}
                    <ConcernCountBadge summary={s.concerns} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{s.account.name}</td>
                  <td className="px-3 py-2">
                    <TierTag tier={s.account.tier} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <IntensityIndicator level={s.intensity} />
                      <span className="text-xs text-muted-foreground">L{s.intensity}</span>
                      <IntensityHoursHint intensity={s.intensity} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <PodChips
                      podSize={s.podSize}
                      assignments={s.assignments}
                      positionDisciplines={s.positionDisciplines ?? {}}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <FitBadge fit={s.fit} />
                  </td>
                  <td className="px-3 py-2 text-sm text-muted-foreground">{s.status}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={canDelete ? 8 : 7}
                    className="px-6 py-12 text-center text-sm text-muted-foreground"
                  >
                    No solutions match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openId && <SolutionDetailDrawer id={openId} onClose={() => setOpenId(null)} />}

      {canDelete && selected.size > 0 && (
        <div className="sticky bottom-4 z-20 mx-auto flex w-fit items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-lg">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--fit-at-risk)] px-3 py-1 text-sm font-medium text-background hover:brightness-95"
          >
            <Trash2 className="h-4 w-4" /> Delete permanently
          </button>
        </div>
      )}

      <DeleteSolutionsDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        ids={[...selected]}
        onDeleted={() => setSelected(new Set())}
      />
    </PageContainer>
  );
}
